/**
 * Quiz Navigation and State Management
 * Handles step-by-step navigation, answer validation, and state persistence
 */

import { convertSchemaToHtml } from 'https://cdn.jsdelivr.net/npm/@thebeyondgroup/shopify-rich-text-renderer';

class QuizManager {
  constructor() {
    this.currentStep = 1;
    this.totalSteps = 4;
    this.answers = {
      glove_style: '',
      hand_size: '',
      glove_size: '',
      moisture: '',
      feel: '',
      texture: ''
    };
    this.valueMap = this.loadValueMappings();
    this.leadFormEnabled = false;
    this.leadFormSubmitted = false;
    this.klaviyoPublicKey = '';
    this.leadDuplicateMessage = '';
    this.quizStartedTracked = false;
    this.quizResultTracked = false;
    
    this.init();
  }

  init() {
    this.cacheElements();
    // Hide wrapper to prevent flicker until initial state is applied
    const wrapper = document.querySelector('.quiz-wrapper');
    if (wrapper) {
      wrapper.style.visibility = 'hidden';
      this.leadFormEnabled = wrapper.dataset.leadFormEnabled === 'true';
      this.klaviyoPublicKey = wrapper.dataset.klaviyoPublicKey || '';
      this.leadDuplicateMessage =
        wrapper.dataset.leadDuplicateMessage ||
        'This email has already been used to unlock results. Please use a different email address.';
    }

    this.attachEventListeners();

    // Check if quiz was completed before loading answers
    let wasCompleted = false;
    try {
      wasCompleted = localStorage.getItem('quiz_completed') === 'true';
    } catch (e) {
      // ignore
    }
    
    this.loadSavedAnswers();

    if (this.sizeNoticeCombined) {
      this.sizeNoticeCombined.style.display = 'none';
    }

    if (wasCompleted) {
      if (!this.leadFormEnabled) {
        // Restoring a finished quiz is not a new start or completion.
        this.quizStartedTracked = true;
        this.quizResultTracked = true;
        this.showResults();
      } else {
        try {
          localStorage.removeItem('quiz_completed');
        } catch (e) {
        }
      }
    }
    
    // Ensure all answer fields are strings, not undefined
    Object.keys(this.answers).forEach(key => {
      if (this.answers[key] === undefined || this.answers[key] === null) {
        this.answers[key] = '';
      }
    });

    // Unhide wrapper after state is applied (next frame to ensure classes are set)
    requestAnimationFrame(() => {
      const wrapperEl = document.querySelector('.quiz-wrapper');
      if (wrapperEl) {
        wrapperEl.style.visibility = '';
      }
    });
  }

  cacheElements() {
    this.tabs = document.querySelectorAll('.quiz-tab');
    this.steps = document.querySelectorAll('.quiz-step');
    this.nextButtons = document.querySelectorAll('.quiz-next-btn');
    this.selects = document.querySelectorAll('.quiz-select');
    // Select only buttons with data-quiz-field, not select elements
    this.optionButtons = document.querySelectorAll('button[data-quiz-field]');
    
    // Mobile navigation elements
    this.mobileNav = document.querySelector('.quiz-mobile-nav');
    this.prevArrow = document.getElementById('quiz-prev-arrow');
    this.nextArrow = document.getElementById('quiz-next-arrow');
    this.mobileTabTitle = document.querySelector('.quiz-mobile-tab__title');
    this.mobileTabProgress = document.querySelector('.quiz-mobile-tab__progress');
    this.sizeNoticeCombined = document.getElementById('quiz-size-notice-combined');
    this.leadForm = document.getElementById('quiz-lead-form');
    this.leadFormElement = document.getElementById('quiz-lead-form-element');
    this.leadFormSubmitButton = document.getElementById('quiz-lead-submit');
    this.leadFormError = document.getElementById('quiz-lead-error');
    this.leadFormSuccess = document.getElementById('quiz-lead-success');
  }

  attachEventListeners() {
    // Next button clicks
    this.nextButtons.forEach(btn => {
      btn.addEventListener('click', () => this.goToNextStep());
    });


    // Tab clicks (allow going back to previous steps)
    this.tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const targetStep = parseInt(e.currentTarget.dataset.step);
        if (targetStep < this.currentStep) {
          this.goToStep(targetStep);
        }
      });
    });

    // Dropdown selections (Size step)
    this.selects.forEach(select => {
      select.addEventListener('change', (e) => {
        const field = e.target.name;
        const value = e.target.value;
        
        // Handle mutual exclusion between glove_size and hand_size
        this.handleSizeSelection(field, value);
        
        this.updateAnswer(field, value);
        this.validateStep(this.currentStep);
        this.updateMobileNavigation();
      });
    });

    this.optionButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const field = e.currentTarget.dataset.quizField;
        const value = e.currentTarget.dataset.value;

        const currentStep = e.currentTarget.closest('.quiz-step');
        if (currentStep) {
          const siblings = currentStep.querySelectorAll('[data-quiz-field="' + field + '"]');
          siblings.forEach(sibling => sibling.classList.remove('active'));
        } else {
          const siblings = e.currentTarget.parentElement.querySelectorAll('[data-quiz-field="' + field + '"]');
          siblings.forEach(sibling => sibling.classList.remove('active'));
        }
        
        e.currentTarget.classList.add('active');
        
        this.updateAnswer(field, value);
        this.validateStep(this.currentStep);
        this.updateMobileNavigation();
      });
    });

    // Mobile navigation arrows
    if (this.prevArrow) {
      this.prevArrow.addEventListener('click', () => this.goToPreviousStep());
    }
    
    if (this.nextArrow) {
      this.nextArrow.addEventListener('click', () => this.goToNextStep());
    }

    // Restart quiz (bind for both desktop and mobile buttons)
    const restartButtons = document.querySelectorAll('#quiz-restart-btn');
    if (restartButtons && restartButtons.length) {
      restartButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.restartQuiz();
        });
      });
    }

    // Lead form submit (only on qr-quiz template when form exists)
    if (this.leadFormElement && this.leadFormSubmitButton && this.leadFormEnabled) {
      this.leadFormElement.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleLeadFormSubmit();
      });
    }
  }

  updateAnswer(field, value) {
    // Ensure value is a string, treat null/undefined as empty string
    this.answers[field] = value || '';

    if (value && !this.quizStartedTracked) {
      this.quizStartedTracked = true;
      this.trackQuizEvent('quiz_started');
    }

    this.saveAnswers();
  }

  /**
   * Handle mutual exclusion between glove_size and hand_size
   * When one is selected, clear and disable the other
   */
  handleSizeSelection(selectedField, selectedValue) {
    const gloveSizeSelect = document.querySelector('select[name="glove_size"]');
    const handSizeSelect = document.querySelector('select[name="hand_size"]');
    const gloveSizeDropdown = document.querySelector('.quiz-size-group:first-child .quiz-dropdown');
    const handSizeDropdown = document.querySelector('.quiz-size-group:nth-child(3) .quiz-dropdown');

    if (!gloveSizeSelect || !handSizeSelect) return;
    
    // If a value is selected (not empty), disable the other field
    if (selectedValue && selectedValue !== '') {
      if (selectedField === 'glove_size') {
        // Clear hand size if it had a value
        const hadValue = !!this.answers.hand_size;
        handSizeSelect.value = '';
        if (handSizeDropdown) {
          handSizeDropdown.classList.remove('disabled');
          const selectedDisplay = handSizeDropdown.querySelector('.quiz-dropdown__selected');
          if (selectedDisplay) {
            selectedDisplay.textContent = 'Select One';
          }
        }
        this.answers.hand_size = '';
        if (hadValue && this.sizeNoticeCombined) this.sizeNoticeCombined.style.display = 'block';
      } else if (selectedField === 'hand_size') {
        // Clear glove size if it had a value
        const hadValue = !!this.answers.glove_size;
        gloveSizeSelect.value = '';
        if (gloveSizeDropdown) {
          gloveSizeDropdown.classList.remove('disabled');
          const selectedDisplay = gloveSizeDropdown.querySelector('.quiz-dropdown__selected');
          if (selectedDisplay) {
            selectedDisplay.textContent = 'Select One';
          }
        }
        this.answers.glove_size = '';
        if (hadValue && this.sizeNoticeCombined) this.sizeNoticeCombined.style.display = 'block';
      }
    } else {
      // Value cleared - nothing to disable; ensure UI looks enabled
      if (gloveSizeDropdown) gloveSizeDropdown.classList.remove('disabled');
      if (handSizeDropdown) handSizeDropdown.classList.remove('disabled');
      if (this.sizeNoticeCombined) this.sizeNoticeCombined.style.display = 'none';
    }
    
    this.saveAnswers();
  }

  /**
   * Apply mutual exclusion logic based on current answers
   * Used when loading saved answers
   */
  applySizeExclusion() {
    const gloveSizeValue = this.answers.glove_size;
    const handSizeValue = this.answers.hand_size;
    // If both somehow present, prefer the last non-empty by clearing the other deterministically: keep hand_size
    if (gloveSizeValue && handSizeValue) {
      this.answers.glove_size = '';
      const gloveSizeSelect = document.querySelector('select[name="glove_size"]');
      const gloveSizeDropdown = document.querySelector('.quiz-size-group:first-child .quiz-dropdown');
      if (gloveSizeSelect) gloveSizeSelect.value = '';
      if (gloveSizeDropdown) {
        const selectedDisplay = gloveSizeDropdown.querySelector('.quiz-dropdown__selected');
        if (selectedDisplay) selectedDisplay.textContent = 'Select One';
      }
      if (this.sizeNoticeCombined) this.sizeNoticeCombined.style.display = 'none';
    }
  }

  validateStep(step) {
    let isValid = false;
    const currentStepEl = document.querySelector(`.quiz-step[data-step="${step}"]`);
    const nextBtn = currentStepEl?.querySelector('.quiz-next-btn');
    
    switch(step) {
      case 1: // Size step - hand_size alone, or glove_size + glove_style
        isValid = !!this.answers.hand_size || (!!this.answers.glove_size && !!this.answers.glove_style);
        break;
      case 2: // Climate step - requires moisture
        isValid = !!this.answers.moisture;
        break;
      case 3: // Feel step
        isValid = !!this.answers.feel;
        break;
      case 4: // Texture step
        isValid = !!this.answers.texture;
        break;
    }

    if (nextBtn) {
      nextBtn.disabled = !isValid;
    }

    return isValid;
  }

  goToStep(step) {
    // Hide all steps
    this.steps.forEach(s => s.classList.remove('active'));
    
    // Show target step
    const targetStep = document.querySelector(`.quiz-step[data-step="${step}"]`);
    if (targetStep) {
      targetStep.classList.add('active');
    }

    // Update tabs
    this.tabs.forEach(tab => {
      const tabStep = parseInt(tab.dataset.step);
      tab.classList.remove('active', 'completed');
      
      if (tabStep === step) {
        tab.classList.add('active');
      } else if (tabStep < step) {
        tab.classList.add('completed');
      }
    });

    this.currentStep = step;
    // Hide size notice when leaving step 1
    if (this.sizeNoticeCombined && step !== 1) {
      this.sizeNoticeCombined.style.display = 'none';
    }
    
    // Update mobile navigation
    this.updateMobileNavigation();
    
    // Validate the current step after navigation
    this.validateStep(step);
    
    // Scroll to quiz-tabs on mobile devices when navigating to a new step
    if (window.innerWidth <= 699) {
      const tabsEl = document.querySelector('.quiz-tabs');
      if (tabsEl) {
        setTimeout(() => {
          tabsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
    
    // Back button removed
  }

  goToNextStep() {
    if (this.currentStep < this.totalSteps) {
      this.goToStep(this.currentStep + 1);
    } else if (this.currentStep === this.totalSteps) {
      // Show results
      this.showResults();
    }
  }

  goToPreviousStep() {
    if (this.currentStep > 1) {
      this.goToStep(this.currentStep - 1);
    }
  }

  updateMobileNavigation() {
    if (!this.mobileNav) return;

    const stepNames = {
      1: 'Size',
      2: 'Climate', 
      3: 'Feel',
      4: 'Texture'
    };

    // Update mobile tab title and progress
    if (this.mobileTabTitle) {
      this.mobileTabTitle.textContent = stepNames[this.currentStep] || 'Step';
    }
    
    if (this.mobileTabProgress) {
      this.mobileTabProgress.textContent = `${this.currentStep} out of ${this.totalSteps}`;
    }

    // Update arrow states
    if (this.prevArrow) {
      this.prevArrow.disabled = this.currentStep <= 1;
    }

    if (this.nextArrow) {
      // Next arrow is enabled only if current step is valid
      const isCurrentStepValid = this.isStepValid(this.currentStep);
      this.nextArrow.disabled = !isCurrentStepValid || this.currentStep >= this.totalSteps;
    }
  }

  /**
   * Check if step is valid without updating UI
   */
  isStepValid(step) {
    switch(step) {
      case 1: // Size step - hand_size alone, or glove_size + glove_style
        return !!this.answers.hand_size || (!!this.answers.glove_size && !!this.answers.glove_style);
      case 2: // Climate step - requires moisture
        return !!this.answers.moisture;
      case 3: // Feel step
        return !!this.answers.feel;
      case 4: // Texture step
        return !!this.answers.texture;
    }
    return false;
  }

  trackQuizEvent(eventName, params = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      quiz_name: 'grip_quiz',
      quiz_page: window.location.pathname,
      ...params
    });
  }

  trackQuizResult(products, status) {
    if (this.quizResultTracked) return;
    this.quizResultTracked = true;

    const recommendations = Array.isArray(products)
      ? products.filter(Boolean)
      : products
        ? [products]
        : [];
    const topProduct = recommendations[0] || null;
    const gripSize = topProduct ? this.calculateSizeRecommendation(topProduct) : '';
    const params = {
      quiz_result: topProduct?.title || status || 'no_match',
      quiz_result_handle: topProduct?.handle || ''
    };

    if (gripSize) params.grip_size = gripSize;

    this.trackQuizEvent('quiz_completed', params);
    this.sendQuizSubmission(recommendations, gripSize);
  }

  answerLabel(value) {
    if (!value) return '';
    return this.mapValue(value) || this.formatValue(value);
  }

  sendQuizSubmission(products, gripSize) {
    const submissionId = window.crypto && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const recommendedGrips = (products || [])
      .map((product) => product?.title)
      .filter(Boolean)
      .join(', ');

    const payload = {
      submission_id: submissionId,
      submitted_at: new Date().toISOString(),
      glove_size: this.answerLabel(this.answers.glove_size),
      hand_size: this.answerLabel(this.answers.hand_size),
      glove_style: this.answerLabel(this.answers.glove_style),
      moisture: this.answerLabel(this.answers.moisture),
      feel: this.answerLabel(this.answers.feel),
      texture: this.answerLabel(this.answers.texture),
      recommended_grip: recommendedGrips,
      recommended_size: gripSize || ''
    };

    fetch('https://hook.us2.make.com/qf1nepw78uhpvc115vucvmt8k3cjixn4', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    }).catch((error) => {
      console.error('Quiz sheet submission failed', error);
    });
  }

  async showResults() {
    if (this.leadFormEnabled && !this.leadFormSubmitted) {
      this.showLeadForm();
      return;
    }

    // Show results step
    this.steps.forEach(s => s.classList.remove('active'));
    const resultsStep = document.querySelector('.quiz-results');
    if (resultsStep) {
      resultsStep.classList.add('active');
    }

    // Mark all tabs as completed
    this.tabs.forEach(tab => {
      tab.classList.remove('active');
      tab.classList.add('completed');
    });

    // Hide mobile navigation on results page
    if (this.mobileNav) {
      this.mobileNav.style.display = 'none';
    }

    if (this.sizeNoticeCombined) {
      this.sizeNoticeCombined.style.display = 'none';
    }

    this.currentStep = 5;

    // Persist completion state for reloads
    try {
      localStorage.setItem('quiz_completed', 'true');
    } catch (e) {
      // ignore
    }

    // Display answer summary
    this.displayAnswerSummary();

    // Get product recommendation
    await this.getProductRecommendation();
  }

  showLeadForm() {
    if (!this.leadForm) {
      this.leadFormEnabled = false;
      this.showResults();
      return;
    }

    const stepsWrapper = document.querySelector('.quiz-steps');
    if (stepsWrapper) {
      stepsWrapper.style.display = 'none';
    }

    this.leadForm.classList.add('quiz-lead-form--active');
    this.leadForm.setAttribute('aria-hidden', 'false');
  }

  async handleLeadFormSubmit() {
    if (!this.leadFormElement || !this.leadFormSubmitButton) return;

    const nameInput = this.leadFormElement.querySelector('#quiz-lead-name');
    const emailInput = this.leadFormElement.querySelector('#quiz-lead-email');

    const name = nameInput?.value.trim() || '';
    const email = emailInput?.value.trim() || '';

    if (this.leadFormError) {
      this.leadFormError.style.display = 'none';
      this.leadFormError.textContent = '';
    }
    if (this.leadFormSuccess) {
      this.leadFormSuccess.style.display = 'none';
      this.leadFormSuccess.textContent = '';
    }

    if (!name) {
      if (this.leadFormError) {
        this.leadFormError.textContent = 'Please enter your full name.';
        this.leadFormError.style.display = 'block';
      }
      nameInput?.focus();
      return;
    }

    const normalizedEmail = email.toLowerCase();
    let usedEmails = [];
    try {
      const stored = localStorage.getItem('quiz_lead_emails');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          usedEmails = parsed;
        }
      }
    } catch (e) {
      usedEmails = [];
    }

    if (usedEmails.includes(normalizedEmail)) {
      if (this.leadFormError) {
        this.leadFormError.textContent =
          this.leadDuplicateMessage ||
          'This email has already been used to unlock results. Please use a different email address.';
        this.leadFormError.style.display = 'block';
      }
      emailInput?.focus();
      return;
    }

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (this.leadFormError) {
        this.leadFormError.textContent = 'Please enter a valid email address.';
        this.leadFormError.style.display = 'block';
      }
      emailInput?.focus();
      return;
    }

    this.leadFormSubmitButton.disabled = true;
    const originalLabel = this.leadFormSubmitButton.textContent;
    this.leadFormSubmitButton.textContent = 'Submitting...';

    try {
      const quizResult = {
        title: 'Grip Quiz Result',
        description: this.getAnswerSummaryHTML(),
        score: null,
        recommendation: ''
      };

      try {
        let topProduct = null;
        let retries = 0;
        const maxRetries = 10;

        while (typeof QuizFilter === 'undefined' && retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
        }

        if (typeof QuizFilter !== 'undefined') {
          const quizFilter = new QuizFilter(this.answers);
          topProduct = await quizFilter.getTopRecommendation();
        } else {
          console.error('QuizFilter not loaded for lead form quizResult logging');
        }

        let sizeRecommendation = null;
        if (topProduct) {
          sizeRecommendation = this.calculateSizeRecommendation(topProduct);
          quizResult.recommendation = topProduct.title || '';
        }

      } catch (debugError) {
        console.error('Error while building/logging quizResult debug data', debugError);
      }

      const success = await this.sendQuizResultToKlaviyo(email, name, quizResult);

      if (success) {
        // Store this email as used on this device
        try {
          const updated = Array.from(new Set([...(usedEmails || []), normalizedEmail]));
          localStorage.setItem('quiz_lead_emails', JSON.stringify(updated));
        } catch (e) {
          // ignore storage errors
        }

        this.leadFormSubmitted = true;
        if (this.leadFormSuccess) {
          this.leadFormSuccess.textContent = 'Thank you! Unlocking your results...';
          this.leadFormSuccess.style.display = 'block';
        }

        if (this.leadForm) {
          this.leadForm.classList.remove('quiz-lead-form--active');
          this.leadForm.setAttribute('aria-hidden', 'true');
        }

        const stepsWrapper = document.querySelector('.quiz-steps');
        if (stepsWrapper) {
          stepsWrapper.style.display = '';
        }

        await this.showResults();
      } else {
        if (this.leadFormError) {
          this.leadFormError.textContent = 'Something went wrong while submitting the form. Please try again.';
          this.leadFormError.style.display = 'block';
        }
      }
    } catch (e) {
      if (this.leadFormError) {
        this.leadFormError.textContent = 'Unexpected error occurred. Please try again.';
        this.leadFormError.style.display = 'block';
      }
    } finally {
      this.leadFormSubmitButton.disabled = false;
      this.leadFormSubmitButton.textContent = originalLabel;
    }
  }

  async sendQuizResultToKlaviyo(userEmail, userName, quizResult) {
    if (!this.klaviyoPublicKey) {
      return true;
    }

    const payload = {
      data: {
        type: 'event',
        attributes: {
          properties: {
            result_title: quizResult.title,
            result_description: quizResult.description,
            score: quizResult.score,
            recommendation: quizResult.recommendation,
            answers: this.answers
          },
          metric: {
            data: {
              type: 'metric',
              attributes: {
                name: 'Quiz Completed'
              }
            }
          },
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: userEmail,
                first_name: userName
              }
            }
          }
        }
      }
    };

    const url = `https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(this.klaviyoPublicKey)}`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'revision': '2024-06-15',
          'content-type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      return response.status === 202;
    } catch (e) {
      console.error('Error sending Klaviyo quiz event', e);
      return false;
    }
  }

  displayAnswerSummary() {
    const summaryContainer = document.getElementById('quiz-answer-summary');
    if (!summaryContainer) return;

    let html = '';

    // Glove Type
    if (this.answers.glove_style) {
      html += `
        <div class="quiz-answer-item">
          <span class="quiz-answer-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_4_220)">
              <path d="M6.79998 22.8C6.79998 23.4627 7.33724 24 7.99998 24H13.6C14.2627 24 14.8 23.4627 14.8 22.8V21.636C15.07 20.1438 15.711 18.7435 16.664 17.564L20.752 12.292C21.2932 11.5917 21.1643 10.5852 20.464 10.044C19.7637 9.50277 18.7572 9.63171 18.216 10.332L16.252 12.872C16.1465 13.0219 15.9653 13.0994 15.784 13.072C15.384 12.972 15.12 12.184 15.096 10.968C15.096 9.856 15.096 9.744 16.344 2.72C16.4923 2.13676 16.3008 1.51961 15.8484 1.12278C15.3959 0.725954 14.7591 0.616571 14.2001 0.839692C13.6412 1.06281 13.2548 1.58069 13.2 2.18L12.8 4.496V1.6C12.8012 1.15477 12.6168 0.729188 12.2911 0.42556C11.9655 0.121932 11.528 -0.0322772 11.084 3.71784e-06C10.2288 0.0815189 9.58144 0.809146 9.59998 1.668V4.748L9.17198 2.368C9.09964 1.77922 8.70749 1.27879 8.15309 1.06777C7.59868 0.856747 6.97303 0.969778 6.52751 1.36145C6.08199 1.75312 5.88973 2.35913 6.02798 2.936L6.69198 6.644L6.24398 5.6C6.02442 5.06079 5.52929 4.68339 4.95116 4.6146C4.37303 4.54582 3.80317 4.79649 3.46321 5.26914C3.12325 5.74178 3.06687 6.36179 3.31598 6.888C5.41598 11.824 6.79998 17.432 6.79998 22.8ZM7.07198 16.8H11.2C11.8627 16.8 12.4 17.3373 12.4 18C12.4 18.6627 11.8627 19.2 11.2 19.2H7.40798C7.31998 18.4 7.19998 17.6 7.07198 16.8ZM14 22.8C14 23.0209 13.8209 23.2 13.6 23.2H7.99998C7.77907 23.2 7.59998 23.0209 7.59998 22.8V22H14V22.8ZM16.884 13.36L18.848 10.82C19.0238 10.5935 19.307 10.478 19.5911 10.5169C19.8751 10.5559 20.1168 10.7435 20.2251 11.0089C20.3333 11.2744 20.2918 11.5775 20.116 11.804L16.032 17.076C15.0665 18.2804 14.3929 19.6919 14.064 21.2H7.59998C7.57598 20.8 7.53598 20.4 7.49998 20H11.2C12.3046 20 13.2 19.1046 13.2 18C13.2 16.8954 12.3046 16 11.2 16H6.93198C6.87598 15.696 6.81198 15.396 6.74798 15.092C9.53288 14.8583 12.1906 13.8233 14.4 12.112C14.572 13.112 14.972 13.692 15.6 13.848C16.0869 13.9545 16.5907 13.763 16.884 13.36ZM4.30398 5.56C4.50212 5.4198 4.7532 5.37713 4.98654 5.44402C5.21987 5.51091 5.41021 5.68012 5.50398 5.904L7.33998 10.236C7.4219 10.4198 7.62756 10.5141 7.82026 10.4563C8.01296 10.3984 8.13274 10.2065 8.09998 10.008L6.79998 2.8C6.72045 2.35818 7.01415 1.93553 7.45598 1.856C7.89781 1.77647 8.32045 2.07018 8.39998 2.512L9.61998 9.31201C9.63986 9.53292 9.83507 9.69589 10.056 9.67601C10.2769 9.65612 10.4399 9.46092 10.42 9.24V1.668C10.4052 1.23656 10.7169 0.862876 11.144 0.800004C11.3657 0.784446 11.5839 0.861812 11.7462 1.01356C11.9086 1.16531 12.0005 1.37776 12 1.6V9.236C11.9812 9.45692 12.1451 9.65123 12.366 9.67C12.5869 9.68878 12.7812 9.52492 12.8 9.304L14 2.312C14.0263 2.01174 14.2191 1.75173 14.4988 1.6394C14.7785 1.52706 15.0976 1.58146 15.3242 1.78012C15.5509 1.97879 15.6467 2.28799 15.572 2.58C14.372 9.584 14.276 9.844 14.32 11.14C13.32 12.048 10.808 13.888 6.59598 14.304C5.98113 11.6564 5.13522 9.06779 4.06798 6.568C3.90412 6.21785 4.00171 5.80102 4.30398 5.56Z" fill="black"/>
              <path d="M11.1998 18.4C11.4207 18.4 11.5998 18.2209 11.5998 18C11.5998 17.7791 11.4207 17.6 11.1998 17.6C10.9789 17.6 10.7998 17.7791 10.7998 18C10.7998 18.2209 10.9789 18.4 11.1998 18.4Z" fill="black"/>
              </g>
              <defs>
              <clipPath id="clip0_4_220">
              <rect width="24" height="24" fill="white"/>
              </clipPath>
              </defs>
            </svg>

          </span>
          <div class="quiz-answer-info">
            <strong>Glove Type</strong>
            <span>${this.mapValue(this.answers.glove_style) || this.formatValue(this.answers.glove_style)}</span>
          </div>
        </div>
      `;
    }

    // Glove/Hand Size (prefer glove_size, fallback to hand_size)
    if (this.answers.glove_size || this.answers.hand_size) {
      const sizeValue = this.answers.glove_size || this.answers.hand_size;
      html += `
        <div class="quiz-answer-item">
          <span class="quiz-answer-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_4_220)">
              <path d="M6.79998 22.8C6.79998 23.4627 7.33724 24 7.99998 24H13.6C14.2627 24 14.8 23.4627 14.8 22.8V21.636C15.07 20.1438 15.711 18.7435 16.664 17.564L20.752 12.292C21.2932 11.5917 21.1643 10.5852 20.464 10.044C19.7637 9.50277 18.7572 9.63171 18.216 10.332L16.252 12.872C16.1465 13.0219 15.9653 13.0994 15.784 13.072C15.384 12.972 15.12 12.184 15.096 10.968C15.096 9.856 15.096 9.744 16.344 2.72C16.4923 2.13676 16.3008 1.51961 15.8484 1.12278C15.3959 0.725954 14.7591 0.616571 14.2001 0.839692C13.6412 1.06281 13.2548 1.58069 13.2 2.18L12.8 4.496V1.6C12.8012 1.15477 12.6168 0.729188 12.2911 0.42556C11.9655 0.121932 11.528 -0.0322772 11.084 3.71784e-06C10.2288 0.0815189 9.58144 0.809146 9.59998 1.668V4.748L9.17198 2.368C9.09964 1.77922 8.70749 1.27879 8.15309 1.06777C7.59868 0.856747 6.97303 0.969778 6.52751 1.36145C6.08199 1.75312 5.88973 2.35913 6.02798 2.936L6.69198 6.644L6.24398 5.6C6.02442 5.06079 5.52929 4.68339 4.95116 4.6146C4.37303 4.54582 3.80317 4.79649 3.46321 5.26914C3.12325 5.74178 3.06687 6.36179 3.31598 6.888C5.41598 11.824 6.79998 17.432 6.79998 22.8ZM7.07198 16.8H11.2C11.8627 16.8 12.4 17.3373 12.4 18C12.4 18.6627 11.8627 19.2 11.2 19.2H7.40798C7.31998 18.4 7.19998 17.6 7.07198 16.8ZM14 22.8C14 23.0209 13.8209 23.2 13.6 23.2H7.99998C7.77907 23.2 7.59998 23.0209 7.59998 22.8V22H14V22.8ZM16.884 13.36L18.848 10.82C19.0238 10.5935 19.307 10.478 19.5911 10.5169C19.8751 10.5559 20.1168 10.7435 20.2251 11.0089C20.3333 11.2744 20.2918 11.5775 20.116 11.804L16.032 17.076C15.0665 18.2804 14.3929 19.6919 14.064 21.2H7.59998C7.57598 20.8 7.53598 20.4 7.49998 20H11.2C12.3046 20 13.2 19.1046 13.2 18C13.2 16.8954 12.3046 16 11.2 16H6.93198C6.87598 15.696 6.81198 15.396 6.74798 15.092C9.53288 14.8583 12.1906 13.8233 14.4 12.112C14.572 13.112 14.972 13.692 15.6 13.848C16.0869 13.9545 16.5907 13.763 16.884 13.36ZM4.30398 5.56C4.50212 5.4198 4.7532 5.37713 4.98654 5.44402C5.21987 5.51091 5.41021 5.68012 5.50398 5.904L7.33998 10.236C7.4219 10.4198 7.62756 10.5141 7.82026 10.4563C8.01296 10.3984 8.13274 10.2065 8.09998 10.008L6.79998 2.8C6.72045 2.35818 7.01415 1.93553 7.45598 1.856C7.89781 1.77647 8.32045 2.07018 8.39998 2.512L9.61998 9.31201C9.63986 9.53292 9.83507 9.69589 10.056 9.67601C10.2769 9.65612 10.4399 9.46092 10.42 9.24V1.668C10.4052 1.23656 10.7169 0.862876 11.144 0.800004C11.3657 0.784446 11.5839 0.861812 11.7462 1.01356C11.9086 1.16531 12.0005 1.37776 12 1.6V9.236C11.9812 9.45692 12.1451 9.65123 12.366 9.67C12.5869 9.68878 12.7812 9.52492 12.8 9.304L14 2.312C14.0263 2.01174 14.2191 1.75173 14.4988 1.6394C14.7785 1.52706 15.0976 1.58146 15.3242 1.78012C15.5509 1.97879 15.6467 2.28799 15.572 2.58C14.372 9.584 14.276 9.844 14.32 11.14C13.32 12.048 10.808 13.888 6.59598 14.304C5.98113 11.6564 5.13522 9.06779 4.06798 6.568C3.90412 6.21785 4.00171 5.80102 4.30398 5.56Z" fill="black"/>
              <path d="M11.1998 18.4C11.4207 18.4 11.5998 18.2209 11.5998 18C11.5998 17.7791 11.4207 17.6 11.1998 17.6C10.9789 17.6 10.7998 17.7791 10.7998 18C10.7998 18.2209 10.9789 18.4 11.1998 18.4Z" fill="black"/>
              </g>
              <defs>
              <clipPath id="clip0_4_220">
              <rect width="24" height="24" fill="white"/>
              </clipPath>
              </defs>
            </svg>
          </span>
          <div class="quiz-answer-info">
            <strong>Glove/Hand Size</strong>
            <span>${this.mapValue(sizeValue) || this.formatValue(sizeValue)}</span>
          </div>
        </div>
      `;
    }

    // Moisture
    if (this.answers.moisture) {
      html += `
        <div class="quiz-answer-item">
          <span class="quiz-answer-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_4_271)">
              <path d="M16.6684 15.1875H16.7996C16.9871 15.1875 17.1184 15.0375 17.1184 14.8688C17.1184 14.6813 16.9684 14.55 16.7996 14.55H16.6684C16.4809 14.55 16.3496 14.7 16.3496 14.8688C16.3496 15.0563 16.4996 15.1875 16.6684 15.1875Z" fill="black"/>
              <path d="M18.0561 15.9H18.1873C18.3748 15.9 18.5061 15.75 18.5061 15.5813C18.5061 15.4125 18.3561 15.2625 18.1873 15.2625H18.0561C17.8686 15.2625 17.7373 15.4125 17.7373 15.5813C17.7373 15.75 17.8873 15.9 18.0561 15.9Z" fill="black"/>
              <path d="M16.9877 16.05C16.8002 16.05 16.6689 16.2 16.6689 16.3688C16.6689 16.5375 16.8189 16.6875 16.9877 16.6875H17.1189C17.3064 16.6875 17.4377 16.5375 17.4377 16.3688C17.4377 16.2 17.2877 16.05 17.1189 16.05H16.9877Z" fill="black"/>
              <path d="M19.9125 17.2313C20.3062 15.5625 19.275 13.875 17.6063 13.4813C15.9563 13.0875 14.2875 14.1 13.875 15.75C12.45 15.375 11.0062 15.1688 9.525 15.15V11.4563L18.1125 7.55626C18.2812 7.48126 18.3563 7.29376 18.2812 7.12501C18.2437 7.05001 18.1875 6.99376 18.1125 6.95626L9.525 3.05626V2.17501H9.6375C10.2188 2.17501 10.7062 1.68751 10.7062 1.10626C10.7062 0.525006 10.2188 0.0375061 9.6375 0.0375061H6.91875C6.3375 0.0375061 5.85 0.525006 5.85 1.10626C5.85 1.68751 6.3375 2.17501 6.91875 2.17501H7.03125V15.2813C2.925 15.75 0.09375 17.4938 0.09375 19.5563C0.09375 22.0313 4.10625 23.9625 9.24375 23.9625C12.4125 23.9625 15.375 23.175 17.025 21.9C17.4938 21.9563 17.9437 21.975 18.4125 21.975C21.5437 21.975 23.9062 20.925 23.9062 19.5563C23.9062 18.4313 22.3125 17.5125 19.9125 17.2313ZM9.525 3.76876L17.1937 7.25626L9.525 10.7438V3.76876ZM16.8937 14.0438C18.2625 14.0438 19.35 15.15 19.35 16.5C19.35 17.8688 18.2437 18.9563 16.8937 18.9563C15.525 18.9563 14.4375 17.85 14.4375 16.5C14.4187 15.15 15.525 14.0625 16.8937 14.0438ZM4.81875 19.5563C4.81875 19.1625 5.6625 18.6375 7.03125 18.4313V20.6813C5.64375 20.475 4.81875 19.95 4.81875 19.5563ZM9.525 18.4313C10.8938 18.6375 11.7375 19.1438 11.7375 19.5563C11.7375 19.9688 10.8938 20.475 9.525 20.6813V18.4313ZM6.50625 1.10626C6.50625 0.881256 6.69375 0.693756 6.91875 0.693756H9.6375C9.8625 0.693756 10.05 0.881256 10.05 1.10626C10.05 1.33126 9.8625 1.51876 9.6375 1.51876H6.91875C6.69375 1.51876 6.50625 1.33126 6.50625 1.10626ZM8.8875 2.17501V20.7563C8.49375 20.775 8.08125 20.775 7.6875 20.7563V2.17501H8.8875ZM18.4125 21.3188C17.925 21.3188 17.4562 21.2813 16.9688 21.225C16.875 21.2063 16.8 21.2438 16.725 21.3C15.225 22.5375 12.3562 23.3063 9.24375 23.3063C4.63125 23.3063 0.75 21.5813 0.75 19.5375C0.75 17.85 3.375 16.35 7.0125 15.9V17.7563C5.26875 18 4.14375 18.675 4.14375 19.5375C4.14375 20.4375 5.3625 21.15 7.275 21.3563H7.29375C7.6125 21.3938 7.93125 21.4125 8.25 21.4125C8.56875 21.4125 8.8875 21.3938 9.20625 21.3563H9.225C11.1375 21.15 12.3563 20.4375 12.3563 19.5375C12.3563 18.675 11.25 18 9.4875 17.7563V15.7875C10.9312 15.8063 12.3562 15.9938 13.725 16.3875V16.5188C13.725 18.2438 15.1125 19.6313 16.8375 19.6313C18.0375 19.6313 19.1438 18.9375 19.65 17.85C21.8813 18.0938 23.2125 18.8813 23.2125 19.5563C23.25 20.4 21.2625 21.3188 18.4125 21.3188Z" fill="black"/>
              </g>
              <defs>
              <clipPath id="clip0_4_271">
              <rect width="24" height="24" fill="white"/>
              </clipPath>
              </defs>
            </svg>

          </span>
          <div class="quiz-answer-info">
            <strong>Moisture</strong>
            <span>${this.formatValue(this.answers.moisture)}</span>
          </div>
        </div>
      `;
    }

    // Feel/Firmness
    if (this.answers.feel) {
      html += `
        <div class="quiz-answer-item">
          <span class="quiz-answer-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_4_278)">
              <path d="M7.41797 4.52054L8.91842 4.1693L9.08942 4.8998L7.58897 5.25104L7.41797 4.52054Z" fill="black"/>
              <path d="M5.91797 4.87128L6.66758 4.6958L6.83852 5.42606L6.08891 5.60154L5.91797 4.87128Z" fill="black"/>
              <path d="M2.28188 11.25H1.84313C1.35442 11.2494 0.885901 11.055 0.540366 10.7094C0.194832 10.3638 0.000498354 9.8952 2.16141e-06 9.40649V8.18137C-0.000664373 7.663 0.152842 7.15614 0.441002 6.72524L0.813002 6.16687C0.865455 6.08795 0.945843 6.03181 1.038 6.00974L5.163 5.04712L5.334 5.77762L1.3545 6.70649L1.065 7.14112C0.859167 7.44896 0.74952 7.81105 0.750002 8.18137V9.40649C0.7503 9.69635 0.865552 9.97426 1.07048 10.1793C1.2754 10.3843 1.55327 10.4996 1.84313 10.5H2.28188C2.47531 10.4998 2.66524 10.4484 2.83231 10.3509C2.99938 10.2534 3.13761 10.1134 3.2329 9.94502C3.3282 9.77669 3.37715 9.5861 3.37478 9.39268C3.3724 9.19926 3.31878 9.00993 3.21938 8.84399L2.30363 7.31812C2.27318 7.26745 2.25514 7.21031 2.25097 7.15134C2.24681 7.09238 2.25665 7.03326 2.27968 6.97882C2.30272 6.92438 2.33829 6.87616 2.38351 6.83809C2.42873 6.80001 2.48231 6.77317 2.53988 6.75974L8.53988 5.36699L8.71013 6.09749L3.21113 7.37249L3.8625 8.45812C4.03019 8.73787 4.12068 9.05709 4.12473 9.38322C4.12878 9.70935 4.04625 10.0307 3.88556 10.3145C3.72487 10.5984 3.49177 10.8345 3.21004 10.9988C2.92831 11.1632 2.60803 11.2498 2.28188 11.25Z" fill="black"/>
              <path d="M16.042 3.61816L17.1668 3.35601L17.3371 4.08643L16.2122 4.34859L16.042 3.61816Z" fill="black"/>
              <path d="M21.8353 3.05063L21.665 2.32013L23.2502 1.95226V1.59751L21.461 2.01526L21.29 1.28476L23.54 0.759759C23.595 0.746937 23.6522 0.746707 23.7074 0.759088C23.7625 0.771469 23.8141 0.79614 23.8583 0.831258C23.9026 0.866377 23.9383 0.911036 23.9629 0.961902C23.9874 1.01277 24.0002 1.06852 24.0002 1.12501V2.25001C24.0002 2.33467 23.9715 2.41685 23.9189 2.48317C23.8663 2.54948 23.7927 2.59604 23.7103 2.61526L21.8353 3.05063Z" fill="black"/>
              <path d="M15.5088 2.62939L17.1699 2.24054L17.3409 2.97104L15.6798 3.35989L15.5088 2.62939Z" fill="black"/>
              <path d="M12.75 9.75C11.8552 9.74901 10.9973 9.39311 10.3646 8.76039C9.73189 8.12767 9.37599 7.2698 9.375 6.375V4.5H10.125V6.375C10.1258 7.07095 10.4026 7.73817 10.8947 8.23028C11.3868 8.72239 12.0541 8.99921 12.75 9C14.2283 9 15.375 7.79025 15.375 6.75V4.875H16.125V6.75C16.125 8.166 14.6816 9.75 12.75 9.75Z" fill="black"/>
              <path d="M11.25 5.62501C11.1853 5.62496 11.1216 5.60815 11.0653 5.57621C11.009 5.54427 10.9619 5.4983 10.9286 5.44276L10.4786 4.69276L11.1218 4.30689L11.4315 4.82364C12.2335 4.63623 13.0519 4.52784 13.875 4.50001C14.6309 4.52399 15.3841 4.60257 16.1287 4.73514L15.825 4.22776C15.7026 4.01922 15.5343 3.84132 15.3329 3.70759C15.1315 3.57386 14.9022 3.48782 14.6625 3.45601C14.1522 3.39719 13.6387 3.37014 13.125 3.37501C11.0527 3.37501 10.125 3.94014 10.125 4.50001H9.375C9.375 4.06014 9.64537 2.62501 13.125 2.62501C13.6699 2.62011 14.2146 2.64916 14.7559 2.71201C15.1083 2.75787 15.4456 2.88347 15.7421 3.07926C16.0387 3.27504 16.2867 3.53586 16.4674 3.84189L17.1964 5.05689C17.2343 5.11919 17.2532 5.19121 17.2509 5.26411C17.2485 5.33701 17.2249 5.40763 17.183 5.46733C17.1411 5.52703 17.0827 5.57321 17.0149 5.60022C16.9471 5.62723 16.873 5.63389 16.8015 5.61939C15.8364 5.42439 14.8583 5.30092 13.875 5.25001C13.0203 5.28759 12.1715 5.40943 11.3408 5.61376C11.3111 5.6212 11.2806 5.62498 11.25 5.62501Z" fill="black"/>
              <path d="M9.75 7.5C9.35218 7.5 8.97064 7.34196 8.68934 7.06066C8.40804 6.77936 8.25 6.39782 8.25 6V5.625C8.25 5.32663 8.36853 5.04048 8.5795 4.8295C8.79048 4.61853 9.07663 4.5 9.375 4.5H9.75V5.25H9.375C9.27554 5.25 9.18016 5.28951 9.10983 5.35984C9.03951 5.43016 9 5.52554 9 5.625V6C9 6.19891 9.07902 6.38968 9.21967 6.53033C9.36032 6.67098 9.55109 6.75 9.75 6.75V7.5Z" fill="black"/>
              <path d="M9.375 4.875H8.625V3.75C8.625 2.75544 9.02009 1.80161 9.72335 1.09835C10.4266 0.395088 11.3804 0 12.375 0C13.3696 0 14.3234 0.395088 15.0267 1.09835C15.7299 1.80161 16.125 2.75544 16.125 3.75H15.375C15.375 2.95435 15.0589 2.19129 14.4963 1.62868C13.9337 1.06607 13.1706 0.75 12.375 0.75C11.5794 0.75 10.8163 1.06607 10.2537 1.62868C9.69107 2.19129 9.375 2.95435 9.375 3.75V4.875Z" fill="black"/>
              <path d="M12 13.875H10.5C9.90326 13.875 9.33097 13.6379 8.90901 13.216C8.48705 12.794 8.25 12.2217 8.25 11.625C8.25 11.0283 8.48705 10.456 8.90901 10.034C9.33097 9.61205 9.90326 9.375 10.5 9.375H16.125C16.1847 9.37505 16.2434 9.38934 16.2965 9.41667C16.3495 9.44399 16.3952 9.48357 16.4299 9.53213L18.3049 12.1571C18.3415 12.2084 18.3646 12.268 18.3721 12.3305C18.3796 12.393 18.3712 12.4564 18.3478 12.5149C18.3243 12.5733 18.2865 12.6249 18.2378 12.6649C18.1892 12.7049 18.1313 12.7319 18.0694 12.7436L12.0694 13.8686C12.0465 13.8728 12.0233 13.8749 12 13.875ZM10.5 10.125C10.1022 10.125 9.72064 10.283 9.43934 10.5643C9.15804 10.8456 9 11.2272 9 11.625C9 12.0228 9.15804 12.4044 9.43934 12.6857C9.72064 12.967 10.1022 13.125 10.5 13.125H11.9651L17.3531 12.1147L15.9319 10.125H10.5Z" fill="black"/>
              <path d="M18.375 12.75H18V12H18.2625L18.9904 11.5151L17.4904 9.11287L16.5 9.60675V9.75H15.75V9.375C15.75 9.30531 15.7694 9.23699 15.806 9.17771C15.8426 9.11843 15.895 9.07052 15.9574 9.03937L17.4574 8.28937C17.541 8.24714 17.6375 8.23777 17.7277 8.26311C17.8179 8.28844 17.8954 8.34664 17.9449 8.42625L19.8199 11.4262C19.8716 11.509 19.889 11.6085 19.8685 11.7039C19.848 11.7993 19.7911 11.8829 19.71 11.937L18.585 12.687C18.5228 12.7283 18.4497 12.7502 18.375 12.75Z" fill="black"/>
              <path d="M19.8341 11.7956L19.1659 11.4544L19.4407 10.9166C19.7253 10.3584 19.8741 9.74094 19.875 9.11438V5.10001C19.8753 5.01217 19.8558 4.92539 19.8179 4.84612C19.7801 4.76685 19.7249 4.69713 19.6564 4.64213C19.5841 4.58272 19.4994 4.54012 19.4086 4.51742C19.3178 4.49472 19.2231 4.49249 19.1314 4.51088C19.0084 4.53374 18.8954 4.59379 18.8076 4.68291C18.7198 4.77203 18.6615 4.88594 18.6405 5.00926L18.375 9.39788L17.625 9.35213L17.8942 4.93013C17.9288 4.68821 18.0273 4.45988 18.1794 4.26861C18.3315 4.07735 18.5318 3.93007 18.7598 3.84192C18.9877 3.75378 19.235 3.72794 19.4762 3.76709C19.7174 3.80623 19.9439 3.90894 20.1323 4.06463C20.2858 4.19003 20.4097 4.34794 20.4949 4.52698C20.5801 4.70602 20.6245 4.90173 20.625 5.10001V9.11251C20.6238 9.85755 20.4469 10.5918 20.1086 11.2556L19.8341 11.7956Z" fill="black"/>
              <path d="M20.625 5.25H19.875V2.1135C19.875 2.001 19.7145 1.875 19.5 1.875H18C17.7855 1.875 17.625 2.001 17.625 2.1135V3.777L18.6124 4.58475L18.1376 5.16525L17.0126 4.24463C16.9696 4.20946 16.935 4.16518 16.9112 4.11499C16.8874 4.06479 16.875 4.00993 16.875 3.95438V2.1135C16.8946 1.83379 17.0237 1.57309 17.2343 1.38801C17.4449 1.20293 17.7201 1.10842 18 1.125H19.5C19.7799 1.10842 20.0551 1.20293 20.2657 1.38801C20.4763 1.57309 20.6054 1.83379 20.625 2.1135V5.25Z" fill="black"/>
              <path d="M19.1876 15H19.125V14.25H19.1876C19.867 14.2493 20.5183 13.9791 20.9987 13.4987C21.4791 13.0183 21.7493 12.367 21.75 11.6876V8.625H22.5V11.6876C22.4989 12.5658 22.1496 13.4077 21.5286 14.0286C20.9077 14.6496 20.0658 14.9989 19.1876 15Z" fill="black"/>
              <path d="M21.75 7.125H22.5V7.875H21.75V7.125Z" fill="black"/>
              <path d="M21.7497 6.37501C21.7458 5.44028 21.5267 4.519 21.1096 3.68251L21.0391 3.54113L21.7103 3.20663L21.7804 3.34763C22.2496 4.2881 22.4957 5.32403 22.4997 6.37501H21.7497Z" fill="black"/>
              <path d="M19.8747 24H11.2497C11.1903 23.9999 11.1318 23.9858 11.0789 23.9587C11.0261 23.9316 10.9804 23.8924 10.9457 23.8442C10.911 23.796 10.8882 23.7402 10.8793 23.6815C10.8703 23.6228 10.8754 23.5628 10.8942 23.5065L11.2692 22.3815L11.9817 22.6189L11.7702 23.25H19.4997V19.4797C19.4966 17.469 19.0583 15.4828 18.215 13.6575L18.8956 13.3417C19.7844 15.2661 20.2464 17.36 20.2497 19.4797V23.625C20.2497 23.7244 20.2102 23.8198 20.1399 23.8902C20.0696 23.9605 19.9742 24 19.8747 24Z" fill="black"/>
              <path d="M12.2306 21.8685L11.5181 21.6319L12.2355 19.479C12.2589 19.4089 12.261 19.3335 12.2417 19.2621C12.2223 19.1908 12.1824 19.1268 12.1268 19.0781L10.65 17.7791C10.6392 17.77 10.6289 17.7602 10.6193 17.7499C9.09368 16.0749 8.24871 13.8906 8.25 11.625H9C8.99845 13.6965 9.76789 15.6944 11.1585 17.2297L12.6229 18.5149C12.7894 18.6605 12.9091 18.8522 12.9669 19.0657C13.0248 19.2792 13.0182 19.5051 12.948 19.7149L12.2306 21.8685Z" fill="black"/>
              <path d="M19.6729 10.816L20.089 10.1921L22.3386 11.6927L21.9224 12.3166L19.6729 10.816Z" fill="black"/>
              <path d="M18.9336 11.9541L19.3056 11.3031L21.9298 12.8026L21.5578 13.4536L18.9336 11.9541Z" fill="black"/>
              <path d="M20.3245 4.11752L20.1745 3.38252L21.2163 3.17402C21.2686 3.16363 21.318 3.14222 21.3614 3.11121C21.4048 3.08019 21.441 3.04028 21.4678 2.99415C21.4945 2.94802 21.5111 2.89671 21.5164 2.84366C21.5217 2.79062 21.5157 2.73704 21.4986 2.68652L21.22 1.85064C21.1918 1.76585 21.1343 1.69391 21.0578 1.64778C20.9813 1.60165 20.8908 1.58436 20.8026 1.59902L19.1845 1.86864L19.0615 1.12914L20.6793 0.85914C20.9438 0.813391 21.216 0.864476 21.4459 1.00304C21.6759 1.1416 21.8482 1.35837 21.9314 1.61364L22.2096 2.44914C22.2601 2.60044 22.2779 2.76075 22.2617 2.91942C22.2455 3.0781 22.1957 3.23152 22.1157 3.36951C22.0357 3.50749 21.9273 3.62689 21.7976 3.71977C21.668 3.81266 21.52 3.87691 21.3636 3.90827L20.3245 4.11752Z" fill="black"/>
              <path d="M13.125 9H13.875V9.75H13.125V9Z" fill="black"/>
              </g>
              <defs>
              <clipPath id="clip0_4_278">
              <rect width="24" height="24" fill="white"/>
              </clipPath>
              </defs>
            </svg>
          </span>
          <div class="quiz-answer-info">
            <strong>Firmness</strong>
            <span>${this.formatValue(this.answers.feel)}</span>
          </div>
        </div>
      `;
    }

    // Texture
    if (this.answers.texture) {
      html += `
        <div class="quiz-answer-item">
          <span class="quiz-answer-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_4_262)">
              <path d="M11.537 21.6299C11.3398 21.5914 11.1574 21.7249 11.1236 21.9062C11.078 22.125 11.2508 22.3266 11.4682 22.3266C11.6568 22.3266 11.8197 22.1735 11.8197 21.975C11.8197 21.8159 11.7119 21.6667 11.537 21.6299Z" fill="black"/>
              <path d="M12.7266 20.9417C12.7266 20.7824 12.6178 20.6334 12.4435 20.5971C12.228 20.5544 12.0234 20.7183 12.0234 20.9417C12.0234 21.1411 12.1866 21.2933 12.375 21.2933C12.5717 21.2933 12.7266 21.132 12.7266 20.9417Z" fill="black"/>
              <path d="M12.7451 22.5324C12.9357 22.5324 13.0969 22.3771 13.0969 22.1808C13.0969 22.0134 12.9806 21.8704 12.8136 21.8358C12.5975 21.793 12.3936 21.9585 12.3936 22.1808C12.3936 22.3767 12.5541 22.5324 12.7451 22.5324Z" fill="black"/>
              <path d="M13.6519 21.4991C13.8486 21.4991 14.0035 21.3381 14.0035 21.1481C14.0035 20.9274 13.8015 20.7601 13.5831 20.8029C13.4082 20.8392 13.2998 20.9899 13.2998 21.1481C13.2998 21.3389 13.4554 21.4991 13.6519 21.4991Z" fill="black"/>
              <path d="M13.633 19.9089C13.633 19.749 13.524 19.6 13.3503 19.5639C13.1377 19.5212 12.9297 19.6835 12.9297 19.9089C12.9297 20.1004 13.0859 20.2606 13.2812 20.2606C13.4724 20.2606 13.633 20.1061 13.633 19.9089Z" fill="black"/>
              <path d="M19.9573 17.8597L19.4434 17.9837C19.2546 18.0292 19.1385 18.2193 19.1841 18.4081C19.2297 18.5967 19.4197 18.713 19.6083 18.6674L20.1223 18.5432C20.5993 18.4281 21.0866 18.6884 21.2563 19.1486C21.4487 19.6702 21.151 20.2417 20.6143 20.3838L18.7673 20.8723C18.1578 21.0338 17.5107 20.7654 17.1943 20.2197L16.4584 18.9505C16.9332 19.0631 17.401 19.1044 17.926 19.0429C18.1188 19.0204 18.2568 18.8455 18.2341 18.6527C18.2116 18.4599 18.0369 18.3222 17.8439 18.3446C17.4475 18.3911 17.0496 18.368 16.6613 18.2759L15.9724 18.1127L12.4233 11.9919L13.2606 10.5478C13.4291 10.5792 13.5961 10.5695 13.7526 10.5238C13.7526 10.5236 13.7526 10.5236 13.7526 10.5236C14.0029 10.45 14.2287 10.2806 14.3677 10.0407C14.5816 9.67196 18.9314 2.1705 19.2714 1.58402C19.564 1.07974 19.3925 0.434664 18.888 0.142062C18.3847 -0.149625 17.7379 0.022311 17.4461 0.525667L12.5425 8.9822C12.3142 9.37569 12.3658 9.85872 12.6524 10.1949L12.0168 11.2908L11.3816 10.1955C11.6629 9.8644 11.7213 9.37881 11.4913 8.9822L6.58755 0.525484C6.29586 0.022311 5.64895 -0.149625 5.14559 0.142062C4.64242 0.433932 4.47048 1.08066 4.76217 1.58402C5.10421 2.1738 9.45406 9.67544 9.66591 10.0407C9.88619 10.4203 10.3236 10.6318 10.773 10.5478L11.6103 11.9919L8.06118 18.1127L7.37252 18.2759C6.3984 18.5066 5.85861 18.29 4.07644 17.8597C3.25283 17.6607 2.41073 18.1104 2.11758 18.9052C1.7825 19.8142 2.30343 20.8157 3.23965 21.0637C5.25747 21.5976 5.2701 21.622 5.62258 21.622C6.3605 21.622 7.06527 21.2323 7.44778 20.5724C7.58492 20.3358 12.017 12.6928 12.017 12.6928C12.017 12.6928 16.4476 20.3336 16.586 20.5724C16.9687 21.2323 17.6731 21.622 18.4112 21.622C18.7639 21.622 18.7767 21.5974 20.7941 21.0635C21.7285 20.8163 22.252 19.8156 21.9162 18.9052C21.6231 18.1104 20.7808 17.6608 19.9573 17.8597ZM18.0545 0.878328C18.1517 0.710421 18.3676 0.653109 18.5353 0.750337C18.7034 0.847933 18.7607 1.0629 18.6632 1.23117L14.6464 8.15859L14.0332 7.81326L18.0545 0.878328ZM13.1508 9.33486L13.6803 8.42171L14.2935 8.76687L13.7594 9.68789C13.6635 9.85341 13.4496 9.91494 13.2786 9.81588C13.1105 9.7181 13.0532 9.50313 13.1508 9.33486ZM9.38741 8.15859L5.37045 1.23117C5.27322 1.06345 5.33053 0.84775 5.49844 0.750337C5.66616 0.653109 5.88204 0.710421 5.97927 0.878328L10.0006 7.81326L9.38741 8.15859ZM10.755 9.81569C10.5838 9.91494 10.3701 9.85323 10.2742 9.68789L9.74025 8.76687L10.3533 8.42171L10.8828 9.33486C10.9802 9.50277 10.9227 9.71847 10.755 9.81569ZM6.83932 20.2197C6.52291 20.7654 5.87637 21.0336 5.26626 20.8723L3.41946 20.3838C2.88186 20.2413 2.58523 19.6695 2.77731 19.1486C2.94723 18.6875 3.43521 18.4284 3.91128 18.5432C5.71578 18.979 6.36196 19.2385 7.57522 18.9505L6.83932 20.2197Z" fill="black"/>
              <path d="M15.03 20.1377C15.217 20.0854 15.3261 19.8913 15.2736 19.7043C14.5761 17.2154 11.4465 16.4082 9.6266 18.2281C8.30806 19.5468 8.30806 21.6925 9.6266 23.011C10.286 23.6704 11.1519 24.0001 12.0181 24C13.5531 23.9998 14.8696 22.9764 15.2736 21.535C15.3261 21.3479 15.217 21.154 15.03 21.1014C14.8427 21.049 14.6488 21.1582 14.5964 21.3451C14.0453 23.312 11.5677 23.9575 10.1239 22.5137C9.07948 21.4694 9.07948 19.7699 10.1239 18.7254C11.5679 17.2815 14.0455 17.9275 14.5964 19.894C14.649 20.0811 14.8433 20.1901 15.03 20.1377Z" fill="black"/>
              </g>
              <defs>
              <clipPath id="clip0_4_262">
              <rect width="24" height="24" fill="white"/>
              </clipPath>
              </defs>
            </svg>

          </span>
          <div class="quiz-answer-info">
            <strong>Texture</strong>
            <span>${this.formatValue(this.answers.texture)}</span>
          </div>
        </div>
      `;
    }

    summaryContainer.innerHTML = html;
  }

  formatValue(value) {
    if (!value) return '';
    return value
      .replace(/-/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  }

  // Load value mappings from script tag (same source used by QuizFilter)
  loadValueMappings() {
    try {
      const scriptTag = document.querySelector('script[data-quiz-value-mappings]');
      if (!scriptTag) return {};
      const mappings = JSON.parse(scriptTag.textContent);
      const valueMap = {};

      const addMap = (arr) => {
        if (!arr || !Array.isArray(arr)) return;
        arr.forEach(item => {
          if (item && item.value && item.name) {
            valueMap[String(item.value).toLowerCase()] = item.name;
          }
        });
      };

      addMap(mappings.glove_size);
      addMap(mappings.hand_size);
      addMap(mappings.glove_style);

      // Static mappings
      valueMap['yes'] = 'Yes';
      valueMap['no'] = 'No';
      valueMap['soft'] = 'Soft';
      valueMap['firm'] = 'Firm';
      valueMap['in-between'] = 'In Between';
      valueMap['smooth'] = 'Smooth';
      valueMap['rough'] = 'Rough';

      return valueMap;
    } catch (e) {
      return {};
    }
  }

  // Map quiz value to display name using mappings
  mapValue(value) {
    if (!value) return '';
    const key = String(value).toLowerCase();
    return this.valueMap && this.valueMap[key] ? this.valueMap[key] : '';
  }

  async getProductRecommendation() {
    const container = document.getElementById('quiz-product-recommendation');
    if (!container) return;

    try {
      // Wait for QuizFilter to be available if it's not loaded yet
      let retries = 0;
      const maxRetries = 10;
      
      while (typeof QuizFilter === 'undefined' && retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }
      
      // Initialize QuizFilter
      if (typeof QuizFilter !== 'undefined') {
        const quizFilter = new QuizFilter(this.answers);
        const recommendations = await quizFilter.getAllRecommendations(50);

        if (recommendations && recommendations.length) {
          this.displayRecommendations(recommendations, container);
          this.trackQuizResult(recommendations, 'matched');
        } else {
          container.innerHTML = '<p>No products found matching your criteria. Please adjust your selections.</p>';
          this.trackQuizResult([], 'no_match');
        }
      } else {
        console.error('QuizFilter not loaded after retries');
        container.innerHTML = '<p>Unable to load recommendations. Please try again.</p>';
        this.trackQuizResult([], 'error');
      }
    } catch (error) {
      console.error('Error getting recommendation:', error);
      container.innerHTML = '<p>Error loading recommendations. Please try again.</p>';
      this.trackQuizResult([], 'error');
    }
  }

  getProductCardHTML(product) {
    const sizeRecommendation = this.calculateSizeRecommendation(product);
    const collectionUrl = product.collectionHandle ? `/collections/${product.collectionHandle}` : '/collections/all';
    const options = {
      newLineToBreak: true,
      classes: {
        p: 'mt-3 text-base',
        h1: 'mb-4 text-2xl md:text-4xl font-bold',
        h2: 'mb-4 text-xl md:text-3xl',
        h3: 'mb-3 text-lg md:text-2xl',
        h4: 'mb-3 text-base md:text-lg font-medium',
        h5: 'mb-2.5 text-sm md:text-base',
        h6: 'mb-2 text-xs md:text-sm',
        ol: 'my-3 ml-3 flex flex-col gap-y-1 list-decimal list-inside',
        ul: 'my-3 ml-3 flex flex-col gap-y-1 list-disc list-inside',
        li: 'text-sm md:text-base',
        a: 'underline text-blue-500 hover:text-purple-500 transition-all',
        strong: 'font-medium',
        em: 'font-italic',
        br: 'block',
      },
    };

    let descriptionHTML = '';
    let isProductDescriptionFallback = false;

    if (product.descriptionHTML) {
      descriptionHTML = convertSchemaToHtml(product.descriptionHTML, options);
    } else if (product.description) {
      descriptionHTML = product.description;
      isProductDescriptionFallback = true;
    }

    const descriptionBlock = isProductDescriptionFallback
      ? `<div class="quiz-product-description quiz-product-description--expandable is-collapsed" style="--truncate-height: 160px;">
            <div class="quiz-product-description__body">${descriptionHTML}</div>
            <button type="button" class="quiz-product-description__toggle" aria-expanded="false">
              <span class="quiz-product-description__toggle-more">Read More</span>
              <span class="quiz-product-description__toggle-less">Read Less</span>
            </button>
          </div>`
      : `<div class="quiz-product-description">${descriptionHTML}</div>`;

    const initialImage = (product.variants && product.variants.length > 0 && product.variants[0].image) 
      ? product.variants[0].image 
      : product.featured_image;

    return `
      <div class="quiz-product-card">
        <div class="quiz-product-card-wrapper--left">
        <h3 class="quiz-product-title">${product.title}</h3>
          ${sizeRecommendation ? `
          <div class="quiz-size-recommendation quiz-size-recommendation--mobile">
              <span class="quiz-size-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M27.3976 7.60617C24.9606 7.60617 22.9668 9.60002 22.9668 12.0369C22.9668 14.4739 24.9606 16.4677 27.3976 16.4677C29.8345 16.4677 31.8283 14.4739 31.8283 12.0369C31.8283 9.60002 29.8345 7.60617 27.3976 7.60617ZM27.3976 14.9908C25.7729 14.9908 24.4437 13.6616 24.4437 12.0369C24.4437 10.4123 25.7729 9.08309 27.3976 9.08309C29.0222 9.08309 30.3514 10.4123 30.3514 12.0369C30.3514 13.6616 29.0222 14.9908 27.3976 14.9908Z" fill="black"/>
                  <path d="M39.5822 0.590782L16.5422 5.76001C14.9914 5.61232 13.736 6.79386 13.736 8.27078L13.4406 16.8369C13.3668 17.9446 14.1052 19.0523 15.2129 19.4216L19.1268 20.7508C17.5022 25.1077 16.0991 28.8 15.9514 29.76L14.4745 44.0123C14.3268 45.6369 15.5083 47.1139 17.1329 47.2616C18.7575 47.4092 20.2345 46.2277 20.3822 44.6031L21.1945 36.9969L24.6652 45.6369C25.3299 47.1877 27.0283 47.8523 28.5052 47.2616C30.056 46.6708 30.7206 44.8985 30.1299 43.4216L25.3299 31.6062C25.4775 31.2369 25.1822 31.9754 28.2837 23.7046C29.2437 21.0462 27.6929 18.24 25.3299 17.4277L18.6099 15.1385L18.8314 8.49232C18.8314 7.90155 18.6837 7.38463 18.3145 6.94155L36.1852 2.88001C35.7422 4.57847 35.4468 7.0154 36.4068 7.82771C37.5883 8.78771 40.616 7.9754 41.3545 6.79386C42.1668 5.46463 40.8375 2.36309 40.2468 1.03386C40.1729 0.443089 39.656 0.590782 39.5822 0.590782ZM24.9606 18.7569C26.5852 19.2739 27.6929 21.2677 27.0283 23.1139C27.0283 23.1877 23.8529 31.3846 24.0006 31.7539L28.8006 43.8646C29.096 44.6031 28.7268 45.4892 27.9883 45.7846C27.2499 46.08 26.3637 45.7108 26.0683 44.9723L21.4899 33.6C21.1945 32.8615 20.1606 33.0092 20.0868 33.8215L18.9791 44.3815C18.9052 45.12 18.2406 45.7108 17.5022 45.7108C17.4283 45.7108 17.4283 45.7108 17.3545 45.7108C16.5422 45.6369 15.9514 44.8985 16.0252 44.0862L17.5022 29.8339C17.576 29.1692 20.7514 20.7508 20.8252 20.5292C20.9729 20.16 20.8252 19.6431 20.3822 19.4954L15.7299 17.9446C15.2868 17.7969 14.9175 17.3539 14.9914 16.8369L15.2868 8.27078C15.2868 7.68001 15.8037 7.16309 16.4683 7.16309C17.0591 7.16309 17.576 7.68001 17.576 8.34463L17.3545 15.5816C17.3545 15.8769 17.576 16.1723 17.8714 16.32L24.9606 18.7569ZM40.2468 5.98155C39.9514 6.49847 37.9575 6.94155 37.5145 6.72001C37.2191 6.35078 37.4406 4.43078 37.9575 2.51078L39.3606 2.2154C40.0252 3.84001 40.4683 5.53847 40.2468 5.98155Z" fill="black"/>
                  <path d="M7.75427 11.1508C7.75427 6.42462 11.5943 2.58462 16.3204 2.58462C16.7635 2.58462 17.0589 2.28923 17.0589 1.84616C17.0589 1.40308 16.7635 1.1077 16.3204 1.1077C10.782 1.1077 6.27734 5.61231 6.27734 11.1508C6.27734 11.5939 6.57273 11.8892 7.01581 11.8892C7.45888 11.8892 7.75427 11.5939 7.75427 11.1508Z" fill="black"/>
                  <path d="M11.6678 23.4831C9.89548 23.1138 8.49241 21.5631 8.27087 19.7169C8.19702 19.3477 7.82779 19.0523 7.45856 19.0523C7.08933 19.1262 6.79395 19.4954 6.79395 19.8646C7.08933 22.3015 8.93548 24.4431 11.3724 24.96C11.7416 25.0338 12.1847 24.8123 12.2586 24.3692C12.3324 24 12.037 23.5569 11.6678 23.4831Z" fill="black"/>
                </svg>
              </span>
            <div>
              <strong>Grip Size Recommendation</strong>
              <span>${sizeRecommendation}</span>
            </div>
          </div>
        ` : ''}
          <div class="quiz-product-image" data-product-id="${product.id}">
            <img src="${initialImage}" alt="${product.title}" loading="lazy" class="quiz-product-main-image">
            ${product.variants && product.variants.length > 1 ? `
              <div class="quiz-product-colors">
                ${product.variants.map((variant, index) => {
                  const color = variant.color || variant.title;
                  const colorStyle = variant.swatchImage 
                    ? `background-image: url('${variant.swatchImage}');` 
                    : this.getColorStyle(color);
                  return `<button 
                    class="color-dot ${index === 0 ? 'active' : ''}" 
                    style="${colorStyle}" 
                    title="${color}"
                    data-variant-image="${variant.image}"
                    data-variant-id="${variant.id}"
                    aria-label="Select ${color} color"
                  ></button>`;
                }).join('')}
              </div>
            ` : ''}
          </div>
        </div>
        <div class="quiz-product-card-wrapper--right">
        ${sizeRecommendation ? `
          <div class="quiz-size-recommendation quiz-size-recommendation--desktop">
              <span class="quiz-size-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M27.3976 7.60617C24.9606 7.60617 22.9668 9.60002 22.9668 12.0369C22.9668 14.4739 24.9606 16.4677 27.3976 16.4677C29.8345 16.4677 31.8283 14.4739 31.8283 12.0369C31.8283 9.60002 29.8345 7.60617 27.3976 7.60617ZM27.3976 14.9908C25.7729 14.9908 24.4437 13.6616 24.4437 12.0369C24.4437 10.4123 25.7729 9.08309 27.3976 9.08309C29.0222 9.08309 30.3514 10.4123 30.3514 12.0369C30.3514 13.6616 29.0222 14.9908 27.3976 14.9908Z" fill="black"/>
                  <path d="M39.5822 0.590782L16.5422 5.76001C14.9914 5.61232 13.736 6.79386 13.736 8.27078L13.4406 16.8369C13.3668 17.9446 14.1052 19.0523 15.2129 19.4216L19.1268 20.7508C17.5022 25.1077 16.0991 28.8 15.9514 29.76L14.4745 44.0123C14.3268 45.6369 15.5083 47.1139 17.1329 47.2616C18.7575 47.4092 20.2345 46.2277 20.3822 44.6031L21.1945 36.9969L24.6652 45.6369C25.3299 47.1877 27.0283 47.8523 28.5052 47.2616C30.056 46.6708 30.7206 44.8985 30.1299 43.4216L25.3299 31.6062C25.4775 31.2369 25.1822 31.9754 28.2837 23.7046C29.2437 21.0462 27.6929 18.24 25.3299 17.4277L18.6099 15.1385L18.8314 8.49232C18.8314 7.90155 18.6837 7.38463 18.3145 6.94155L36.1852 2.88001C35.7422 4.57847 35.4468 7.0154 36.4068 7.82771C37.5883 8.78771 40.616 7.9754 41.3545 6.79386C42.1668 5.46463 40.8375 2.36309 40.2468 1.03386C40.1729 0.443089 39.656 0.590782 39.5822 0.590782ZM24.9606 18.7569C26.5852 19.2739 27.6929 21.2677 27.0283 23.1139C27.0283 23.1877 23.8529 31.3846 24.0006 31.7539L28.8006 43.8646C29.096 44.6031 28.7268 45.4892 27.9883 45.7846C27.2499 46.08 26.3637 45.7108 26.0683 44.9723L21.4899 33.6C21.1945 32.8615 20.1606 33.0092 20.0868 33.8215L18.9791 44.3815C18.9052 45.12 18.2406 45.7108 17.5022 45.7108C17.4283 45.7108 17.4283 45.7108 17.3545 45.7108C16.5422 45.6369 15.9514 44.8985 16.0252 44.0862L17.5022 29.8339C17.576 29.1692 20.7514 20.7508 20.8252 20.5292C20.9729 20.16 20.8252 19.6431 20.3822 19.4954L15.7299 17.9446C15.2868 17.7969 14.9175 17.3539 14.9914 16.8369L15.2868 8.27078C15.2868 7.68001 15.8037 7.16309 16.4683 7.16309C17.0591 7.16309 17.576 7.68001 17.576 8.34463L17.3545 15.5816C17.3545 15.8769 17.576 16.1723 17.8714 16.32L24.9606 18.7569ZM40.2468 5.98155C39.9514 6.49847 37.9575 6.94155 37.5145 6.72001C37.2191 6.35078 37.4406 4.43078 37.9575 2.51078L39.3606 2.2154C40.0252 3.84001 40.4683 5.53847 40.2468 5.98155Z" fill="black"/>
                  <path d="M7.75427 11.1508C7.75427 6.42462 11.5943 2.58462 16.3204 2.58462C16.7635 2.58462 17.0589 2.28923 17.0589 1.84616C17.0589 1.40308 16.7635 1.1077 16.3204 1.1077C10.782 1.1077 6.27734 5.61231 6.27734 11.1508C6.27734 11.5939 6.57273 11.8892 7.01581 11.8892C7.45888 11.8892 7.75427 11.5939 7.75427 11.1508Z" fill="black"/>
                  <path d="M11.6678 23.4831C9.89548 23.1138 8.49241 21.5631 8.27087 19.7169C8.19702 19.3477 7.82779 19.0523 7.45856 19.0523C7.08933 19.1262 6.79395 19.4954 6.79395 19.8646C7.08933 22.3015 8.93548 24.4431 11.3724 24.96C11.7416 25.0338 12.1847 24.8123 12.2586 24.3692C12.3324 24 12.037 23.5569 11.6678 23.4831Z" fill="black"/>
                </svg>
              </span>
            <div>
              <strong>Grip Size Recommendation</strong>
              <span>${sizeRecommendation}</span>
            </div>
          </div>
        ` : ''}
        ${descriptionBlock}
        
        <div class="quiz-product-actions">
          <a href="${product.url}" class="quiz-btn quiz-btn--primary">View Product</a>
            <a href="/collections/golf-grips" class="quiz-btn quiz-btn--secondary">Shop All Swing Grips</a>
          </div>
        </div>
      </div>
    `;
  }

  displayRecommendations(products, container) {
    const cards = products.map((p) => this.getProductCardHTML(p)).join('');
    const wrapper = `
      <div class="quiz-product-list">
        ${cards}
      </div>
    `;
    container.innerHTML = wrapper;
    this.attachColorSwatchListeners(container);
    this.initExpandableDescriptions(container);
  }

  displayRecommendation(product, container) {
    const html = this.getProductCardHTML(product);
    container.innerHTML = html;
    this.attachColorSwatchListeners(container);
    this.initExpandableDescriptions(container);
  }

  /**
   * Truncate long product.description fallbacks and wire Read More / Read Less.
   * Only applies when custom.short_description metafield is missing.
   */
  initExpandableDescriptions(container) {
    const expandableBlocks = container.querySelectorAll('.quiz-product-description--expandable');

    expandableBlocks.forEach((block) => {
      const body = block.querySelector('.quiz-product-description__body');
      const toggle = block.querySelector('.quiz-product-description__toggle');
      if (!body || !toggle) return;

      const truncateHeight = parseInt(block.style.getPropertyValue('--truncate-height'), 10) || 160;
      let isAnimating = false;

      // If content already fits, no need for Read More
      if (body.scrollHeight <= truncateHeight + 8) {
        block.classList.remove('is-collapsed');
        block.classList.add('is-short');
        body.style.maxHeight = 'none';
        toggle.hidden = true;
        return;
      }

      // Set initial collapsed height for smooth first expand
      body.style.maxHeight = `${truncateHeight}px`;

      toggle.addEventListener('click', () => {
        if (isAnimating) return;
        isAnimating = true;

        const isCollapsed = block.classList.contains('is-collapsed');
        const startHeight = body.offsetHeight;

        if (isCollapsed) {
          // Expand
          body.style.maxHeight = `${startHeight}px`;
          block.classList.remove('is-collapsed');
          toggle.setAttribute('aria-expanded', 'true');

          void body.offsetHeight; // force reflow
          body.style.maxHeight = `${body.scrollHeight}px`;

          const onEnd = (event) => {
            if (event.propertyName !== 'max-height') return;
            body.style.maxHeight = 'none';
            isAnimating = false;
            body.removeEventListener('transitionend', onEnd);
          };
          body.addEventListener('transitionend', onEnd);
        } else {
          // Collapse
          body.style.maxHeight = `${startHeight}px`;
          void body.offsetHeight; // force reflow
          block.classList.add('is-collapsed');
          toggle.setAttribute('aria-expanded', 'false');
          body.style.maxHeight = `${truncateHeight}px`;

          const onEnd = (event) => {
            if (event.propertyName !== 'max-height') return;
            isAnimating = false;
            body.removeEventListener('transitionend', onEnd);
          };
          body.addEventListener('transitionend', onEnd);
        }
      });
    });
  }

  calculateSizeRecommendation(product) {
    if (!product) return 'Standard';


    const isInRange = (value, range) => {
      if (!value || !range) return false;
      
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return false;
      
      const rangeStr = range.toString().trim().toLowerCase();
      
      // Check for "over-X" format (greater than X)
      if (rangeStr.startsWith('over-')) {
        const threshold = parseFloat(rangeStr.replace('over-', ''));
        return !isNaN(threshold) && numValue > threshold;
      }
      
      // Check for "under-X" format (less than X)
      if (rangeStr.startsWith('under-')) {
        const threshold = parseFloat(rangeStr.replace('under-', ''));
        return !isNaN(threshold) && numValue < threshold;
      }
      
      // Check for ">X" format (greater than)
      if (rangeStr.startsWith('>')) {
        const threshold = parseFloat(rangeStr.substring(1));
        return !isNaN(threshold) && numValue > threshold;
      }
      
      // Check for "<X" format (less than)
      if (rangeStr.startsWith('<')) {
        const threshold = parseFloat(rangeStr.substring(1));
        return !isNaN(threshold) && numValue < threshold;
      }

      const dashIndex = rangeStr.indexOf('-', 1);
      if (dashIndex > 0) {
        const minValue = parseFloat(rangeStr.substring(0, dashIndex));
        const maxValue = parseFloat(rangeStr.substring(dashIndex + 1));
        if (!isNaN(minValue) && !isNaN(maxValue)) {
          return numValue >= minValue && numValue <= maxValue;
        }
      }
      
      // If no range pattern matched, do exact comparison
      return value.toString().toLowerCase() === rangeStr;
    };

    const fieldsToMap = (fields) => {
      const map = {};
      for (const f of fields || []) {
        if (f && f.key) map[f.key] = f.value;
      }
      return map;
    };

    const getRecommendedFromMeta = (meta, matchKey, matchValue, useRangeCheck = false) => {
      try {
        const nodes = meta && meta.references && meta.references.nodes ? meta.references.nodes : [];
        for (const node of nodes) {
          const map = fieldsToMap(node && node.fields);

          // Use range check for hand_size, exact match for others
          const isMatch = useRangeCheck 
            ? isInRange(map[matchKey], matchValue)
            : map[matchKey]?.toLowerCase() === matchValue?.toLowerCase();

          if (isMatch && map.recommended_grip_size) {
            return map.recommended_grip_size;
          }
        }
      } catch (e) {
        // ignore and fall through to default
      }
      return null;
    };

    // Normalize for match: "medium-large" ≈ "Medium/Large", "mens-regular" ≈ "Men's Regular"
    const normalizeMatchValue = (value) =>
      String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');

    const valuesMatch = (metaValue, answerValue) => {
      if (!metaValue || !answerValue) return false;
      const candidates = [
        answerValue,
        this.mapValue(answerValue)
      ]
        .filter(Boolean)
        .map(normalizeMatchValue);

      const normalizedMeta = normalizeMatchValue(metaValue);
      return candidates.includes(normalizedMeta);
    };

    // Glove size path: match by glove_size; if multiple rows, prefer glove_type match
    const getRecommendedFromGloveSizeMeta = (meta, gloveSize, gloveType) => {
      try {
        const nodes = meta && meta.references && meta.references.nodes ? meta.references.nodes : [];
        const sizeMatches = [];

        for (const node of nodes) {
          const map = fieldsToMap(node && node.fields);
          if (valuesMatch(map.glove_size, gloveSize) && map.recommended_grip_size) {
            sizeMatches.push(map);
          }
        }

        if (sizeMatches.length === 0) return null;
        if (sizeMatches.length === 1) {
          return sizeMatches[0].recommended_grip_size;
        }

        if (gloveType) {
          const typeMatch = sizeMatches.find((map) => valuesMatch(map.glove_type, gloveType));

          if (typeMatch) {
            return typeMatch.recommended_grip_size;
          }

        }

        return sizeMatches[0].recommended_grip_size;
      } catch (e) {
        // ignore and fall through to default
      }
      return null;
    };

    // If glove_size was selected, look into quiz_recommended_grip_size_and_hand_size
    if (this.answers.glove_size) {
      const rec = getRecommendedFromGloveSizeMeta(
        product.quiz_recommended_grip_size_and_hand_size,
        this.answers.glove_size,
        this.answers.glove_style
      );
      if (rec) return rec;
    }

    // If hand_size was selected, look into quiz_recommended_grip_size_and_glove_size
    if (this.answers.hand_size) {
      const rec = getRecommendedFromMeta(
        product.quiz_recommended_grip_size_and_glove_size,
        'hand_size',
        this.answers.hand_size,
        true // Use range check for hand_size
      );
      if (rec) return rec;
    }

    return 'Standard';
  }

  getColorStyle(colorName) {
    const colorMap = {
      'black': '#000000',
      'white': '#ffffff',
      'red': '#e74c3c',
      'blue': '#3498db',
      'green': '#2ecc71',
      'yellow': '#f1c40f',
      'gray': '#95a5a6',
      'grey': '#95a5a6',
      'orange': '#e67e22',
      'pink': '#e91e63',
      'purple': '#9b59b6',
      'brown': '#795548',
      'navy': '#34495e',
      'lime': '#cddc39'
    };
    
    const color = colorName.toLowerCase().trim();
    
    // Check for exact color match
    for (const [name, code] of Object.entries(colorMap)) {
      if (color.includes(name)) {
        return `background-color: ${code};`;
      }
    }
    
    // Check if it's a hex color
    if (colorName.startsWith('#')) {
      return `background-color: ${colorName};`;
    }
    
    // Default: try to use the color name directly
    return `background-color: ${colorName}; background: linear-gradient(135deg, ${colorName}, ${colorName});`;
  }
  
  attachColorSwatchListeners(container) {
    const colorDots = container.querySelectorAll('.color-dot');
    if (!colorDots.length) return;

    colorDots.forEach(dot => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();

        const card = dot.closest('.quiz-product-card');
        if (!card) return;

        // Remove active only within this product card
        const siblingDots = card.querySelectorAll('.color-dot');
        siblingDots.forEach(d => d.classList.remove('active'));
        dot.classList.add('active');

        // Update image within this product card
        const productImage = card.querySelector('.quiz-product-main-image');
        const variantImage = dot.getAttribute('data-variant-image');

        if (productImage && variantImage && variantImage !== 'null' && variantImage !== 'undefined') {
          productImage.style.opacity = '0.5';
          setTimeout(() => {
            productImage.src = variantImage;
            productImage.style.opacity = '1';
          }, 150);
        }
      });
    });
  }

  getAnswerSummaryHTML() {
    let html = '';

    if (this.answers.glove_style) {
      html += `
        <div class="quiz-answer-item">
          <div class="quiz-answer-info">
            <strong>Glove Type</strong>
            <span>${this.mapValue(this.answers.glove_style) || this.formatValue(this.answers.glove_style)}</span>
          </div>
        </div>
      `;
    }

    if (this.answers.glove_size || this.answers.hand_size) {
      const sizeValue = this.answers.glove_size || this.answers.hand_size;
      html += `
        <div class="quiz-answer-item">
          <div class="quiz-answer-info">
            <strong>Glove/Hand Size</strong>
            <span>${this.mapValue(sizeValue) || this.formatValue(sizeValue)}</span>
          </div>
        </div>
      `;
    }

    if (this.answers.moisture) {
      html += `
        <div class="quiz-answer-item">
          <div class="quiz-answer-info">
            <strong>Moisture</strong>
            <span>${this.formatValue(this.answers.moisture)}</span>
          </div>
        </div>
      `;
    }

    if (this.answers.feel) {
      html += `
        <div class="quiz-answer-item">
          <div class="quiz-answer-info">
            <strong>Firmness</strong>
            <span>${this.formatValue(this.answers.feel)}</span>
          </div>
        </div>
      `;
    }

    if (this.answers.texture) {
      html += `
        <div class="quiz-answer-item">
          <div class="quiz-answer-info">
            <strong>Texture</strong>
            <span>${this.formatValue(this.answers.texture)}</span>
          </div>
        </div>
      `;
    }

    return html;
  }

  saveAnswers() {
    try {
      localStorage.setItem('quiz_answers', JSON.stringify(this.answers));
    } catch (e) {
      console.error('Could not save answers:', e);
    }
  }

  loadSavedAnswers() {
    try {
      const saved = localStorage.getItem('quiz_answers');
      if (saved) {
        const savedAnswers = JSON.parse(saved);
        
        // Check if saved data is valid
        let isValidData = true;
        Object.keys(this.answers).forEach(key => {
          if (savedAnswers[key] === undefined) {
            isValidData = false;
          }
        });
        
        // If data is invalid, clear it
        if (!isValidData) {
          localStorage.removeItem('quiz_answers');
          return;
        }
        
        // Restore answers - merge with defaults to ensure all fields exist
        Object.keys(this.answers).forEach(key => {
          // Use saved value if it exists and is not null, otherwise keep default empty string
          if (savedAnswers[key] !== undefined && savedAnswers[key] !== null) {
            this.answers[key] = savedAnswers[key];
            
            // Restore UI state only if value is not empty
            if (savedAnswers[key]) {
              const select = document.querySelector(`[name="${key}"]`);
              if (select) {
                select.value = savedAnswers[key];
                
                // Update custom dropdown display if it exists
                const dropdown = select.closest('.quiz-size-group')?.querySelector('.quiz-dropdown');
                if (dropdown && window.QuizDropdown) {
                  // Find the dropdown instance and update its display
                  const dropdownButton = dropdown.querySelector('.quiz-dropdown__button');
                  const selectedDisplay = dropdown.querySelector('.quiz-dropdown__selected');
                  const selectedOption = select.querySelector(`option[value="${savedAnswers[key]}"]`);
                  
                  if (selectedOption && selectedDisplay) {
                    selectedDisplay.textContent = selectedOption.textContent;
                  }
                  
                  // Mark the option as selected in the dropdown
                  const dropdownOption = dropdown.querySelector(`[data-value="${savedAnswers[key]}"]`);
                  if (dropdownOption) {
                    dropdown.querySelectorAll('.quiz-dropdown__option').forEach(opt => {
                      opt.classList.remove('quiz-dropdown__option--selected');
                    });
                    dropdownOption.classList.add('quiz-dropdown__option--selected');
                  }
                }
              }
              
              const button = document.querySelector(`[data-quiz-field="${key}"][data-value="${savedAnswers[key]}"]`);
              if (button) {
                button.classList.add('active');
              }
            }
          }
        });

        // Apply mutual exclusion logic after restoring all answers
        this.applySizeExclusion();

        // Only validate and update navigation if quiz is not completed
        const isCompleted = localStorage.getItem('quiz_completed') === 'true';
        if (!isCompleted) {
          // Validate current step after restoring state
          this.validateStep(this.currentStep);
          this.updateMobileNavigation();
        }
      }
    } catch (e) {
      console.error('Could not load saved answers:', e);
      // Clear corrupted data
      localStorage.removeItem('quiz_answers');
    }
  }

  restartQuiz() {
    // Allow lead form to be shown again after restart
    this.leadFormSubmitted = false;
    this.quizStartedTracked = false;
    this.quizResultTracked = false;

    // Clear answers
    this.answers = {
      glove_style: '',
      hand_size: '',
      glove_size: '',
      moisture: '',
      feel: '',
      texture: ''
    };
    
    // Clear localStorage
    localStorage.removeItem('quiz_answers');
    localStorage.removeItem('quiz_completed');
    
    // Reset native select elements
    this.selects.forEach(select => {
      select.value = '';
      select.selectedIndex = 0;
      select.disabled = false;
      select.classList.remove('disabled');
    });

    const gloveSizeButton = document.querySelector('.quiz-size-group:first-child .quiz-dropdown__button');
    const handSizeButton = document.querySelector('.quiz-size-group:nth-child(3) .quiz-dropdown__button');
    gloveSizeButton.removeAttribute('disabled');
    handSizeButton.removeAttribute('disabled');
    
    // Reset custom dropdowns
    const customDropdowns = document.querySelectorAll('.quiz-dropdown');
    customDropdowns.forEach(dropdown => {
      const selectedDisplay = dropdown.querySelector('.quiz-dropdown__selected');
      if (selectedDisplay) {
        selectedDisplay.textContent = 'Select One';
      }
      
      // Remove selected class from all options
      const options = dropdown.querySelectorAll('.quiz-dropdown__option');
      options.forEach(opt => {
        opt.classList.remove('quiz-dropdown__option--selected');
      });
      
      // Remove disabled state
      dropdown.classList.remove('disabled');
    });
    
    // Hide size notice
    if (this.sizeNoticeCombined) this.sizeNoticeCombined.style.display = 'none';
    
    if (this.leadFormElement) {
      const nameInput = this.leadFormElement.querySelector('#quiz-lead-name');
      const emailInput = this.leadFormElement.querySelector('#quiz-lead-email');
      if (nameInput) nameInput.value = '';
      if (emailInput) emailInput.value = '';
    }
    if (this.leadFormError) {
      this.leadFormError.style.display = 'none';
      this.leadFormError.textContent = '';
    }
    if (this.leadFormSuccess) {
      this.leadFormSuccess.style.display = 'none';
      this.leadFormSuccess.textContent = '';
    }
    if (this.leadForm) {
      this.leadForm.classList.remove('quiz-lead-form--active');
      this.leadForm.setAttribute('aria-hidden', 'true');
    }
    
    // Reset option buttons
    this.optionButtons.forEach(btn => btn.classList.remove('active'));
    
    // Show mobile navigation again (rely on CSS; clear inline styles)
    if (this.mobileNav) {
      this.mobileNav.style.display = '';
    }
    
    // Go back to step 1
    this.goToStep(1);

    // Scroll to the top of quiz-tabs
    const tabsEl = document.querySelector('.quiz-tabs');
    if (tabsEl) {
      tabsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}

// Initialize quiz when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new QuizManager();
  });
} else {
  new QuizManager();
}

